import React from "react";
import { Box, Container, Text, Flex, HStack } from "@chakra-ui/react";
import { HashLink } from "react-router-hash-link";
import { motion } from "framer-motion";

const MotionBox = motion(Box);

export const Header = () => {
  return (
    <Box
      id="header"
      bg="#293452"
      position="relative"
      overflow="hidden"
      minH="100vh"
      display="flex"
      alignItems="center"
      className="dot-grid"
    >
      {/* Radial glow — center */}
      <Box
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%,-50%)"
        w={{ base: "600px", md: "900px" }}
        h={{ base: "600px", md: "900px" }}
        borderRadius="50%"
        background="radial-gradient(ellipse, rgba(0,149,200,0.12) 0%, transparent 70%)"
        pointerEvents="none"
      />

      {/* Concentric circles — top right */}
      <Box position="absolute" top="-60px" right="-60px" pointerEvents="none">
        <Box
          w={{ base: "280px", md: "420px" }}
          h={{ base: "280px", md: "420px" }}
          borderRadius="50%"
          border="52px solid #0095C8"
          opacity="0.45"
        />
        <Box
          position="absolute"
          top="60px"
          left="60px"
          w={{ base: "160px", md: "300px" }}
          h={{ base: "160px", md: "300px" }}
          borderRadius="50%"
          border="2px solid rgba(0,149,200,0.25)"
        />
      </Box>

      {/* Concentric circles — bottom left */}
      <Box position="absolute" bottom="-80px" left="-80px" pointerEvents="none">
        <Box
          w={{ base: "220px", md: "340px" }}
          h={{ base: "220px", md: "340px" }}
          borderRadius="50%"
          border="44px solid #0095C8"
          opacity="0.2"
        />
      </Box>

      <Container
        maxW="1200px"
        px={{ base: "5", md: "8" }}
        pt={{ base: "120px", md: "0" }}
        pb={{ base: "80px", md: "0" }}
        position="relative"
        zIndex={1}
      >
        <Flex direction="column" align="center" textAlign="center" gap={{ base: 8, md: 10 }}>

          {/* Label pill */}
          <MotionBox
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Box
              display="inline-flex"
              alignItems="center"
              gap={2}
              px={4}
              py={2}
              borderRadius="full"
              border="1px solid rgba(0,149,200,0.4)"
              bg="rgba(0,149,200,0.1)"
            >
              <Box w="6px" h="6px" borderRadius="50%" bg="#0095C8" />
              <Text
                fontFamily="'Inter', sans-serif"
                fontSize="0.8rem"
                fontWeight="600"
                letterSpacing="0.1em"
                textTransform="uppercase"
                color="#0095C8"
              >
                INC Curriculum Aligned
              </Text>
            </Box>
          </MotionBox>

          {/* Headline */}
          <MotionBox
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            maxW="760px"
          >
            <Text
              as="h1"
              fontFamily="'Public Sans', sans-serif"
              fontWeight="700"
              fontSize={{ base: "2.25rem", sm: "3rem", md: "3.75rem", lg: "4rem" }}
              lineHeight="1.15"
              color="white"
            >
              Clarity for your exams.{" "}
              <Box
                as="span"
                bgGradient="linear(135deg, #0095C8, #809CFF)"
                bgClip="text"
              >
                Confidence for the ward.
              </Box>
            </Text>
          </MotionBox>

          {/* Subheadline */}
          <MotionBox
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            maxW="600px"
          >
            <Text
              fontFamily="'Inter', sans-serif"
              fontSize={{ base: "1rem", md: "1.125rem" }}
              color="rgba(255,255,255,0.72)"
              lineHeight="1.7"
            >
              Structured nursing study guides and concept-driven MCQs — built from
              standard textbooks and aligned to the Indian Nursing Council curriculum.
            </Text>
          </MotionBox>

          {/* CTAs */}
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Flex
              gap={4}
              justify="center"
              align="center"
              direction={{ base: "column", sm: "row" }}
            >
              <HashLink smooth to="/#contact">
                <Box
                  as="button"
                  px={{ base: "28px", md: "36px" }}
                  py={{ base: "14px", md: "16px" }}
                  borderRadius="8px"
                  background="linear-gradient(135deg, #0095C8, #016293)"
                  color="white"
                  fontFamily="'Public Sans', sans-serif"
                  fontWeight="600"
                  fontSize={{ base: "1rem", md: "1.0625rem" }}
                  letterSpacing="0.02em"
                  boxShadow="0 4px 20px rgba(0,149,200,0.4)"
                  _hover={{
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 32px rgba(0,149,200,0.5)",
                  }}
                  _active={{ transform: "translateY(0)", boxShadow: "0 2px 12px rgba(0,149,200,0.3)" }}
                  transition="all 0.25s cubic-bezier(0.4,0,0.2,1)"
                  whiteSpace="nowrap"
                >
                  Start with Free MCQs
                </Box>
              </HashLink>

              <HashLink smooth to="/#how-it-works">
                <Box
                  as="button"
                  px={{ base: "28px", md: "36px" }}
                  py={{ base: "12px", md: "14px" }}
                  borderRadius="8px"
                  bg="transparent"
                  color="white"
                  fontFamily="'Public Sans', sans-serif"
                  fontWeight="600"
                  fontSize={{ base: "1rem", md: "1.0625rem" }}
                  border="2px solid rgba(255,255,255,0.5)"
                  _hover={{ borderColor: "white", bg: "rgba(255,255,255,0.06)" }}
                  transition="all 0.25s ease"
                  whiteSpace="nowrap"
                >
                  See How It Works
                </Box>
              </HashLink>
            </Flex>
          </MotionBox>

          {/* Trust dots */}
          <MotionBox
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <Flex
              gap={{ base: 5, md: 10 }}
              justify="center"
              align="center"
              flexWrap="wrap"
            >
              {[
                "Standard textbook references",
                "INC curriculum-aligned",
                "Concept-driven MCQs",
              ].map((label) => (
                <HStack key={label} spacing={2}>
                  <Box
                    w="7px"
                    h="7px"
                    borderRadius="50%"
                    bgGradient="linear(to-r, #0095C8, #809CFF)"
                    flexShrink={0}
                  />
                  <Text
                    fontFamily="'Inter', sans-serif"
                    fontSize={{ base: "0.8rem", md: "0.875rem" }}
                    color="rgba(255,255,255,0.5)"
                  >
                    {label}
                  </Text>
                </HStack>
              ))}
            </Flex>
          </MotionBox>

        </Flex>
      </Container>
    </Box>
  );
};
